import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ClipboardList, CheckCircle2, FileText, Loader2, Home } from 'lucide-react';

interface CustomerRow {
  client_id: number;
  business_name: string;
  contact_name: string | null;
  contact_email: string | null;
  onboarding_status: string;
  profile_status: string | null;
  property_count: number | null;
  property_type: string | null;
  submitted_at: string | null;
  profile_updated_at: string | null;
  file_count: number;
  agreement_accepted_at: string | null;
}

async function fetchCustomers(): Promise<CustomerRow[]> {
  const res = await fetch('/api/onboarding', { credentials: 'same-origin' });
  if (res.status === 401) { window.location.href = '/login'; throw new Error('unauthorized'); }
  const data = await res.json() as { customers: CustomerRow[]; error?: string };
  if (!res.ok) throw new Error(data.error || 'error');
  return data.customers;
}

const STATUS_STYLES: Record<string, string> = {
  not_started: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-amber-100 text-amber-700',
  submitted: 'bg-teal-100 text-teal-700',
  approved: 'bg-emerald-100 text-emerald-700',
};
const STATUS_LABELS: Record<string, string> = {
  not_started: 'Sin empezar',
  in_progress: 'En progreso',
  submitted: 'Enviado',
  approved: 'Aprobado',
};

export default function AdminOnboardingPage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['admin-onboarding'], queryFn: fetchCustomers });

  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
          <ClipboardList size={20} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Altas de clientes</h1>
          <p className="text-sm text-slate-500">Estado de onboarding y datos enviados por los clientes</p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={28} /></div>
      ) : isError ? (
        <p className="text-rose-600">Error al cargar.</p>
      ) : !data || data.length === 0 ? (
        <p className="text-slate-400 text-center py-16">Todavía no hay clientes en proceso de alta.</p>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                <th className="px-4 py-3 font-medium">Acuerdo</th>
                <th className="px-4 py-3 font-medium">Propiedades</th>
                <th className="px-4 py-3 font-medium">Archivos</th>
                <th className="px-4 py-3 font-medium">Actualizado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map(c => (
                <tr key={c.client_id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/onboarding/${c.client_id}`} className="font-medium text-slate-900 hover:text-teal-700">
                      {c.business_name}
                    </Link>
                    <div className="text-xs text-slate-500">{c.contact_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[c.onboarding_status] ?? STATUS_STYLES.not_started}`}>
                      {STATUS_LABELS[c.onboarding_status] ?? c.onboarding_status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {c.agreement_accepted_at
                      ? <CheckCircle2 size={18} className="text-emerald-500" />
                      : <FileText size={16} className="text-slate-300" />}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {c.property_count != null ? <span className="flex items-center gap-1"><Home size={13} /> {c.property_count}</span> : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.file_count || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {c.profile_updated_at ? new Date(c.profile_updated_at).toLocaleDateString('es-ES') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
